import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pager, ViewToggle, ConfirmModal, PageSpinner } from '@/components/common';
import { levelFromScore } from '@/data/mockCatalog';
import { useFundamentals, getFundamentalName } from '@/hooks/useFundamentals';
import { fmtActionRange } from '@/utils/formatters';
import { useActions, useCreateAction, useUpdateAction, useDeleteAction } from '@/hooks/useActions';
import type { ActionItem } from '@/api/types';
import { ActionFormModal, ALL_LANG_CODES, TRANS_LANGS, LEVEL_LABELS, LEVEL_COLORS } from './ActionFormModal';
import type { FormState, DeleteTarget } from './ActionFormModal';

function makeDefault(): FormState {
  return {
    open: false, editId: null,
    fund: '', thrMin: '1.0', thrMax: '4.0',
    nextlevel: '', title: '', desc: '',
    active: true,
    addedLangs: [],
    previewLang: 'es',
    langTexts: {},
    errors: [],
  };
}

export function AccionesSubTab() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { data: fundamentals = [] } = useFundamentals();
  const [view, setView] = useState<'cards' | 'list'>('cards');
  const [search, setSearch] = useState('');
  const [fundFilter, setFundFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [form, setForm] = useState<FormState>(makeDefault);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { data: actionsData = [], isLoading: loadingActions } = useActions();
  const actions = actionsData as unknown as ActionItem[];
  const createAction = useCreateAction();
  const updateAction = useUpdateAction();
  const deleteAction = useDeleteAction();

  const filtered = actions.filter(a => {
    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase()) ||
                        a.description.toLowerCase().includes(search.toLowerCase());
    const matchFund = !fundFilter || a.fundamentalId === fundFilter;
    return matchSearch && matchFund;
  });
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);
  const fundOf = (id: string) => fundamentals.find(f => f.id === id);

  function openForm(editId: string | null = null) {
    if (editId) {
      const a = actions.find(x => x.id === editId);
      if (a) {
        const lt: Record<string, { nextlevel: string; title: string; desc: string }> = {};
        if (a.translations) {
          for (const [lang, tr] of Object.entries(a.translations)) {
            const trAny = tr as unknown as Record<string, string>;
            lt[lang] = {
              nextlevel: trAny.nextLevelText ?? '',
              title:     trAny.title ?? '',
              desc:      trAny.description ?? '',
            };
          }
        }
        setForm({
          ...makeDefault(),
          open: true, editId,
          fund: a.fundamentalId,
          thrMin: String(a.threshold),
          thrMax: String(a.thresholdMax ?? a.threshold),
          nextlevel: a.nextLevelText ?? '',
          title: a.title,
          desc: a.description,
          active: a.active,
          addedLangs: [...ALL_LANG_CODES],
          langTexts: lt,
        });
        return;
      }
    }
    setForm({ ...makeDefault(), open: true });
  }

  function closeForm() { setForm(makeDefault()); }

  function handleSave() {
    const errs: string[] = [];
    if (!form.fund)  errs.push(t('admin.tipFormFundamental'));
    if (!form.thrMin) errs.push(t('admin.actionRuleMin'));
    if (!form.thrMax) errs.push(t('admin.actionRuleMax'));
    if (!form.title)  errs.push(`${t('admin.actionFormTitle')} (ES)`);
    if (!form.desc)   errs.push(`${t('admin.actionFormDescription')} (ES)`);
    for (const code of form.addedLangs) {
      const lt = form.langTexts[code];
      const langDef = TRANS_LANGS.find(l => l.code === code);
      const label = langDef ? `${langDef.flag} ${langDef.label}` : code.toUpperCase();
      if (!lt?.title) errs.push(`${t('admin.actionFormTitle')} (${label})`);
      if (!lt?.desc)  errs.push(`${t('admin.actionFormDescription')} (${label})`);
    }
    if (errs.length) {
      setForm(f => ({ ...f, errors: errs }));
      return;
    }

    const body: Omit<ActionItem, 'id'> = {
      fundamentalId: form.fund,
      threshold: parseFloat(form.thrMin) || 0,
      thresholdMax: parseFloat(form.thrMax) || parseFloat(form.thrMin) || 0,
      title: form.title,
      description: form.desc,
      nextLevelText: form.nextlevel,
      translations: Object.fromEntries(
        form.addedLangs.map(code => [code, {
          title: form.langTexts[code]?.title ?? '',
          description: form.langTexts[code]?.desc ?? '',
          nextLevelText: form.langTexts[code]?.nextlevel ?? '',
        }])
      ),
      timeSeconds: 0,
      xp: 0,
      active: form.active,
    };

    if (form.editId) {
      updateAction.mutate({ id: form.editId, ...body });
    } else {
      createAction.mutate(body);
    }
    closeForm();
  }

  return (
    <>
      {form.open && (
        <ActionFormModal
          form={form}
          setForm={setForm}
          onClose={closeForm}
          onSave={handleSave}
          fundamentals={fundamentals}
        />
      )}

      {/* ── DELETE CONFIRM ─────────────────────────────────────────────────── */}
      {deleteTarget && (
        <ConfirmModal
          type="danger"
          title={t('admin.actionDeleteTitle')}
          message={t('admin.actionDeleteDesc', { name: deleteTarget.name })}
          confirmText={t('common.delete')}
          onConfirm={() => {
            deleteAction.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* ── TOOLBAR ────────────────────────────────────────────────────────── */}
      <div className="admin-header">
        <h2>{t('admin.actionsTitle')}</h2>
        <div className="tpl-toolbar">
          <ViewToggle view={view} onViewChange={setView} />
          <input
            className="tpl-search-input"
            type="text"
            placeholder={t('admin.actionsSearchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <button className="btn btn-primary btn-small" onClick={() => openForm()}>
            <i className="fas fa-plus" /> {t('admin.createAction')}
          </button>
        </div>
      </div>

      {loadingActions
        ? <PageSpinner minHeight="120px" />
        : (
        <>
          <div className="tips-filters">
            <select
              className="tpl-filter-sel"
              value={fundFilter}
              onChange={e => { setFundFilter(e.target.value); setPage(1); }}
            >
              <option value="">{t('admin.tipsAllFundamentals')}</option>
              {fundamentals.map(f => (
                <option key={f.id} value={f.id}>{getFundamentalName(f, lang)}</option>
              ))}
            </select>
          </div>

          {filtered.length === 0 && (
            <div className="tips-empty">
              <i className="fas fa-rocket" />
              <p>{t('admin.actionsNoResults')}</p>
            </div>
          )}

      {/* ── CARDS ──────────────────────────────────────────────────────────── */}
      {view === 'cards' && filtered.length > 0 && (
        <>
          <div className="tips-cards-grid">
            {paged.map(a => {
              const fund = fundOf(a.fundamentalId);
              const lvlA = levelFromScore(a.threshold);
              const lvlB = levelFromScore(a.thresholdMax ?? a.threshold);
              return (
                <div key={a.id} className="tip-card">
                  {fund && (
                    <div className="tip-card-fund" style={{ background: `${fund.color}22`, color: fund.color }}>
                      {getFundamentalName(fund, lang)}
                    </div>
                  )}
                  <p className="tip-card-name">{a.title}</p>
                  <p className="tip-card-text">{a.description}</p>
                  <div className="tip-card-footer">
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="tip-card-cond">
                        {fmtActionRange(a.threshold, a.thresholdMax ?? a.threshold)}
                      </span>
                      <span
                        className="tip-card-cond"
                        style={{ background: `${LEVEL_COLORS[lvlA]}22`, color: LEVEL_COLORS[lvlA] }}
                      >
                        {LEVEL_LABELS[lvlA]}{lvlA !== lvlB ? ` → ${LEVEL_LABELS[lvlB]}` : ''}
                      </span>
                    </div>
                    <div className="adm-actions">
                      <button className="btn-icon" title={t('common.edit')} onClick={() => openForm(a.id)}>
                        <i className="fas fa-pen" />
                      </button>
                      <button
                        className="btn-icon danger"
                        title={t('common.delete')}
                        onClick={() => setDeleteTarget({ id: a.id, name: a.title })}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pager-cards">
            <Pager
              total={filtered.length} page={page} pageSize={pageSize}
              onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }}
            />
          </div>
        </>
      )}

      {/* ── TABLE ──────────────────────────────────────────────────────────── */}
      {view === 'list' && filtered.length > 0 && (
        <div className="surveys-table-scroll" style={{ marginTop: 12 }}>
          <table className="adm-table">
            <thead>
              <tr>
                <th>{t('admin.colFundamental')}</th>
                <th>{t('admin.colTitle')}</th>
                <th>{t('admin.colDescription')}</th>
                <th>{t('admin.colCondition')}</th>
                <th>{t('admin.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(a => {
                const fund = fundOf(a.fundamentalId);
                const lvlA = levelFromScore(a.threshold);
                return (
                  <tr key={a.id}>
                    <td>
                      {fund && (
                        <span
                          className="tpl-meta-pill"
                          style={{ background: `${fund.color}22`, color: fund.color }}
                        >
                          {getFundamentalName(fund, lang)}
                        </span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>{a.title}</td>
                    <td style={{ fontSize: '0.78rem', color: '#6b7280', maxWidth: 220 }}>{a.description}</td>
                    <td>
                      <span
                        className="tip-card-cond"
                        style={{ background: `${LEVEL_COLORS[lvlA]}22`, color: LEVEL_COLORS[lvlA] }}
                      >
                        {fmtActionRange(a.threshold, a.thresholdMax ?? a.threshold)}
                      </span>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="btn-icon" title={t('common.edit')} onClick={() => openForm(a.id)}>
                          <i className="fas fa-pen" />
                        </button>
                        <button
                          className="btn-icon danger"
                          title={t('common.delete')}
                          onClick={() => setDeleteTarget({ id: a.id, name: a.title })}
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager
            total={filtered.length} page={page} pageSize={pageSize}
            onPage={setPage} onPageSize={s => { setPageSize(s); setPage(1); }}
          />
        </div>
      )}
        </>
      )}
    </>
  );
}

